import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import api from '../services/api';

type Period = '7days' | '1month' | '3months' | '6months' | '1year';

interface ManagerStats {
  manager_id: number;
  manager_name: string;
  manager_email: string;
  total_tasks: number;
  internal_tasks: number;
  api_tasks: number;
}

interface StatsResponse {
  period: string;
  start_date: string;
  end_date: string;
  managers: ManagerStats[];
}

const TasksStatsPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<Record<Period, StatsResponse | null>>({} as Record<Period, StatsResponse | null>);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('7days');

  const loadStats = useCallback(async (period: Period) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/auth/tasks-stats/?period=${period}`);
      setStats(prev => ({ ...prev, [period]: response.data }));
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial period stats
  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadStats(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stats when period changes (lazy loading)
  useEffect(() => {
    if (!stats[selectedPeriod]) {
      loadStats(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  const getPeriodLabel = (period: Period): string => {
    const labels: Record<Period, string> = {
      '7days': 'Last 7 Days',
      '1month': 'Last Month',
      '3months': 'Last 3 Months',
      '6months': 'Last 6 Months',
      '1year': 'Last Year'
    };
    return labels[period];
  };

  // Check if user has access
  if (user && !user.can_view_all_tasks) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners and Administrators can view task statistics.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const currentStats = stats[selectedPeriod];
  const isLoading = loading && !currentStats;

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading statistics...</p>
          </div>
        </div>
      </>
    );
  }

  const totalTasks = currentStats?.managers.reduce((sum, m) => sum + m.total_tasks, 0) || 0;
  const totalInternal = currentStats?.managers.reduce((sum, m) => sum + m.internal_tasks, 0) || 0;
  const totalApi = currentStats?.managers.reduce((sum, m) => sum + m.api_tasks, 0) || 0;

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">📊 Tasks Statistics</h1>
            <p className="mt-2 text-gray-600">
              Overview of tasks by manager
            </p>
          </div>

          {/* Period Selector */}
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Time Period:
            </label>
            <div className="flex flex-wrap gap-2">
              {(['7days', '1month', '3months', '6months', '1year'] as Period[]).map((period) => (
                <button
                  key={period}
                  onClick={() => {
                    setSelectedPeriod(period);
                    // Load stats if not already loaded
                    if (!stats[period]) {
                      loadStats(period);
                    }
                  }}
                  className={`px-6 py-3 rounded-lg font-semibold transition ${
                    selectedPeriod === period
                      ? 'bg-accent-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getPeriodLabel(period)}
                </button>
              ))}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Total Tasks */}
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-100 text-sm font-medium">Total Tasks (Manual + API)</p>
                  <p className="text-3xl font-bold mt-2">{totalTasks}</p>
                </div>
                <div className="bg-primary-400 bg-opacity-30 rounded-full p-3">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Manual Tasks */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Manual (UI)</p>
                  <p className="text-3xl font-bold mt-2">{totalInternal}</p>
                </div>
                <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* API Tasks */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">API Bookings</p>
                  <p className="text-3xl font-bold mt-2">{totalApi}</p>
                </div>
                <div className="bg-purple-400 bg-opacity-30 rounded-full p-3">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Managers List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Tasks by Manager</h2>
              <p className="text-sm text-gray-600 mt-1">
                Showing data for {getPeriodLabel(selectedPeriod).toLowerCase()}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Manual (UI)
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      API Bookings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentStats?.managers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                        No managers found
                      </td>
                    </tr>
                  ) : (
                    currentStats?.managers.map((manager, index) => (
                      <tr key={manager.manager_id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-accent-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {manager.manager_name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {manager.manager_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {manager.manager_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-primary-100 text-primary-700">
                            {manager.total_tasks}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm text-green-700 font-medium">{manager.internal_tasks}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm text-purple-700 font-medium">{manager.api_tasks}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Summary */}
            {currentStats && currentStats.managers.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    Showing {currentStats.managers.length} manager{currentStats.managers.length !== 1 ? 's' : ''}
                  </span>
                  <span className="font-semibold text-gray-900">
                    Total: {totalTasks} task{totalTasks !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Info Cards */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Performer */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">🏆 Top Performer</h3>
              {currentStats && currentStats.managers.length > 0 && currentStats.managers[0].total_tasks > 0 ? (
                <div>
                  <div className="flex items-center mb-3">
                    <div className="h-12 w-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {currentStats.managers[0].manager_name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="ml-3">
                      <p className="font-semibold text-gray-900">{currentStats.managers[0].manager_name}</p>
                      <p className="text-xs text-gray-500">{currentStats.managers[0].manager_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-yellow-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-yellow-800">Total Tasks:</span>
                    <span className="text-2xl font-bold text-yellow-900">{currentStats.managers[0].total_tasks}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="bg-green-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-green-600">Manual</p>
                      <p className="text-lg font-bold text-green-900">{currentStats.managers[0].internal_tasks}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-purple-600">API</p>
                      <p className="text-lg font-bold text-purple-900">{currentStats.managers[0].api_tasks}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No data available</p>
              )}
            </div>

            {/* Period Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">📅 Period Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Period:</span>
                  <span className="font-semibold text-gray-900">{getPeriodLabel(selectedPeriod)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">From:</span>
                  <span className="font-semibold text-gray-900">
                    {currentStats ? new Date(currentStats.start_date).toLocaleDateString() : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">To:</span>
                  <span className="font-semibold text-gray-900">
                    {currentStats ? new Date(currentStats.end_date).toLocaleDateString() : '-'}
                  </span>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                      <p className="text-xs text-green-600">Manual</p>
                      <p className="text-xl font-bold text-green-900">{totalInternal}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg px-3 py-2 text-center">
                      <p className="text-xs text-purple-600">API</p>
                      <p className="text-xl font-bold text-purple-900">{totalApi}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => loadStats(selectedPeriod)}
                    className="w-full px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition text-sm font-medium"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TasksStatsPage;

